import { useMemo, useState } from 'react';
import { newnessYear, pricePerSqFt, streetNumber, fitColor, money } from './homesCalc';

// ============================================================
// $/sq ft (y) vs newness year (x). One dot per house: size = sq ft, color =
// FitScore (gray → green). Comps are hollow, behind a toggle (off by default).
// Tap a dot for its numbers. Fixed drawing width inside a sideways-scrolling
// container, so it stays legible on a phone. Hand-rolled SVG like the Finance
// and Swimming charts; the app has no chart library.
// ============================================================

const W = 640, H = 300;
const PAD = { l: 52, r: 18, t: 16, b: 36 };

function niceTicks(min, max, count = 5) {
  if (!(max > min)) { const p = Math.max(1, Math.abs(min) * 0.05); min -= p; max += p; }
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || raw;
  const lo = Math.floor(min / step) * step, hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v * 1000) / 1000);
  return { lo, hi, ticks };
}

export default function HomesScatter({ watch, comps, fitById }) {
  const [showComps, setShowComps] = useState(false);
  const [picked, setPicked] = useState(null);

  const pts = useMemo(() => {
    const mk = (r, comp) => ({ r, comp, x: newnessYear(r), y: pricePerSqFt(r), s: r.SqFt, fit: comp ? null : fitById.get(r.id) ?? null });
    const all = [...watch.map((r) => mk(r, false)), ...(showComps ? comps.map((r) => mk(r, true)) : [])];
    return { plotted: all.filter((p) => p.x !== null && p.y !== null), missing: all.filter((p) => p.x === null || p.y === null).length };
  }, [watch, comps, fitById, showComps]);

  const { plotted, missing } = pts;
  const xs = plotted.map((p) => p.x), ys = plotted.map((p) => p.y), ss = plotted.map((p) => p.s).filter((s) => s > 0);
  const X = niceTicks(Math.min(...xs), Math.max(...xs), 5);
  const Y = niceTicks(Math.min(...ys), Math.max(...ys), 4);
  const smin = Math.min(...ss), smax = Math.max(...ss);
  const px = (x) => PAD.l + ((x - X.lo) / (X.hi - X.lo || 1)) * (W - PAD.l - PAD.r);
  const py = (y) => H - PAD.b - ((y - Y.lo) / (Y.hi - Y.lo || 1)) * (H - PAD.t - PAD.b);
  const rad = (s) => (s > 0 && smax > smin ? 6 + ((s - smin) / (smax - smin)) * 10 : 10);
  const pick = picked ? plotted.find((p) => p.r.id === picked) : null;

  return (
    <div className="Homes-chart">
      <div className="Homes-chart-head">
        <span className="Homes-chart-title">$/sq ft vs year</span>
        <label className="Homes-chart-toggle">
          <input type="checkbox" checked={showComps} onChange={(e) => setShowComps(e.target.checked)} /> show comps
        </label>
      </div>
      {plotted.length === 0 ? (
        <p className="Homes-chart-empty">Needs rent, sq ft and year built on at least one house.</p>
      ) : (
        <div className="Homes-chart-scroll">
          <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="Rent per square foot against year built or renovated">
            {Y.ticks.map((t) => (
              <g key={`y${t}`}>
                <line x1={PAD.l} x2={W - PAD.r} y1={py(t)} y2={py(t)} className="Homes-grid" />
                <text x={PAD.l - 6} y={py(t) + 4} textAnchor="end" className="Homes-axis">${t.toFixed(2)}</text>
              </g>
            ))}
            {X.ticks.map((t) => (
              <text key={`x${t}`} x={px(t)} y={H - PAD.b + 18} textAnchor="middle" className="Homes-axis">{Math.round(t)}</text>
            ))}
            {/* comps first so watched houses draw on top */}
            {[...plotted].sort((a, b) => (a.comp === b.comp ? 0 : a.comp ? -1 : 1)).map((p) => (
              <g key={p.r.id} className="Homes-dot" onClick={() => setPicked(picked === p.r.id ? null : p.r.id)}>
                <circle cx={px(p.x)} cy={py(p.y)} r={rad(p.s)}
                  fill={p.comp ? 'none' : fitColor(p.fit)} fillOpacity={p.comp ? 0 : 0.85}
                  stroke={p.comp ? '#9a8a9a' : '#fff'} strokeWidth={p.comp ? 1.5 : 1.5}
                  strokeDasharray={p.comp ? '3 2' : undefined} />
                <text x={px(p.x)} y={py(p.y) - rad(p.s) - 3} textAnchor="middle" className={p.comp ? 'Homes-dot-label Homes-dot-label--comp' : 'Homes-dot-label'}>
                  {streetNumber(p.r.Title)}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}
      {pick && (
        <p className="Homes-chart-pick">
          <strong>{pick.r.Title}</strong> · {money(pick.r.LeasePrice)}/mo · ${pick.y.toFixed(2)}/sq ft · {pick.x}
          {pick.r.SqFt ? ` · ${pick.r.SqFt.toLocaleString('en-US')} sq ft` : ''}{pick.comp ? ' · comp' : pick.fit !== null ? ` · fit ${pick.fit}` : ''}
        </p>
      )}
      {missing > 0 && <p className="Homes-chart-note">{missing} not plotted (missing rent, sq ft or year).</p>}
    </div>
  );
}
